/**
 * Canonical placement registry for the embroidery combination-rules engine. Ids are `${location}:${position}`
 */

export type EmbroideryLocation = 'CALF' | 'CHEST' | 'SIDE' | 'SLEEVE' | 'THIGH'

export type EmbroideryPosition = 'LEFT' | 'RIGHT'

export type PlacementId = `${EmbroideryLocation}:${EmbroideryPosition}`

export const EMBROIDERY_LOCATIONS = ['CALF', 'CHEST', 'SIDE', 'SLEEVE', 'THIGH'] as const

export const EMBROIDERY_POSITIONS = ['LEFT', 'RIGHT'] as const

export const toPlacementId = (
  location: EmbroideryLocation,
  position: EmbroideryPosition
): PlacementId => `${location}:${position}`

export const fromPlacementId = (
  id: PlacementId
): { location: EmbroideryLocation; position: EmbroideryPosition } => {
  const [location, position] = id.split(':') as [EmbroideryLocation, EmbroideryPosition]
  return { location, position }
}

const isEmbroideryLocation = (value: string): value is EmbroideryLocation =>
  (EMBROIDERY_LOCATIONS as readonly string[]).includes(value)

const isEmbroideryPosition = (value: string): value is EmbroideryPosition =>
  (EMBROIDERY_POSITIONS as readonly string[]).includes(value)

/** Line-item attribute form of a placement, e.g. `CHEST:LEFT` -> `left_chest`. */
export const toPlacementAttribute = (placementId: PlacementId): string => {
  const { location, position } = fromPlacementId(placementId)
  return `${position}_${location}`.toLowerCase()
}

/** Inverse of `toPlacementAttribute`; undefined for anything the registry can't express. */
export const fromPlacementAttribute = (value: string): PlacementId | undefined => {
  const [position, location, ...rest] = value.toUpperCase().split('_')
  if (rest.length > 0 || !position || !location) return undefined
  if (!isEmbroideryPosition(position) || !isEmbroideryLocation(location)) return undefined
  return toPlacementId(location, position)
}

/** Every id the registry can express. Anatomy narrows this to what a given garment actually has. */
export const ALL_PLACEMENT_IDS: readonly PlacementId[] = EMBROIDERY_LOCATIONS.flatMap(location =>
  EMBROIDERY_POSITIONS.map(position => toPlacementId(location, position))
)
